"use client";
import React, { useState, useEffect, useRef } from "react";
import { getCityBySlug, searchCities } from "../../app/utilities/apis";
import CustomInput from "./CustomInput";
import ImageWrapper from "./ImageWrapper";
import { setLocationCookie } from "../../app/utilities/locationCookie";

export type { LocationSuggestion } from "@/types/city";
import type { LocationSuggestion } from "@/types/city";

interface LocationSearchInputProps {
    value: string;
    onChange: (value: string, selectedLocation: LocationSuggestion | null) => void;
    error?: string;
    placeholder?: string;
    className?: string;
    initialLocation?: LocationSuggestion | null; // optional: for pre-filling
}

const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
    value,
    onChange,
    error,
    placeholder = "Type your city, e.g. Jaipur, New Delhi",
    className = "",
    initialLocation,
}) => {
    const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
    const [isLoadingLocations, setIsLoadingLocations] = useState(false);
    const [showLocationDropdown, setShowLocationDropdown] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState<LocationSuggestion | null>(null);
    const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Pre-fill from initialLocation if provided
    useEffect(() => {
        if (initialLocation && !selectedLocation) {
            const display = initialLocation.name;
            setSelectedLocation(initialLocation);
            onChange(display, initialLocation);
        }
    }, [initialLocation, onChange, selectedLocation]);

    // Cleanup debounce
    useEffect(() => {
        return () => {
            if (debounceTimer) clearTimeout(debounceTimer);
        };
    }, [debounceTimer]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowLocationDropdown(false);
            }
        };

        if (showLocationDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showLocationDropdown]);

    const clearInput = () => {
        onChange("", null);
        setSelectedLocation(null);
        setLocationSuggestions([]);
        setShowLocationDropdown(false);
        inputRef.current?.focus();
    };

    const fetchLocationSuggestionsAPI = async (keyword: string) => {
        const trimmed = keyword.trim();
        if (trimmed.length === 0) {
            setLocationSuggestions([]);
            setShowLocationDropdown(false);
            return;
        }

        setIsLoadingLocations(true);
        try {
            const raw = await searchCities(keyword);
            const dataArray = Array.isArray(raw) ? raw : raw?.data || [];

            if (Array.isArray(dataArray) && dataArray.length > 0) {
                const mapped = dataArray.map((item: any) => {
                    if (item?.name && item?.state?.name) {
                        return {
                            name: item.name,
                            slug: item.slug || item.name.toLowerCase(),
                            state: item.state
                        } as LocationSuggestion;
                    }
                    return null;
                }).filter(Boolean) as LocationSuggestion[];

                setLocationSuggestions(mapped);
                setShowLocationDropdown(mapped.length > 0);
            } else {
                setLocationSuggestions([]);
                setShowLocationDropdown(true);
            }
        } catch (err) {
            console.error("Location suggestions error:", err);
            setLocationSuggestions([]);
            setShowLocationDropdown(true);
        } finally {
            setIsLoadingLocations(false);
        }
    };

    const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let input = e.target.value.replace(/[^A-Za-z0-9\s,.\-'()&]/g, "");
        if (input.startsWith(",")) input = input.slice(1);

        // If user is typing after selection → treat as new search
        if (selectedLocation) {
            setSelectedLocation(null);
        }

        onChange(input, null);

        if (debounceTimer) clearTimeout(debounceTimer);

        const trimmed = input.trim();
        if (trimmed.length >= 3) {
            const timer = setTimeout(() => {
                fetchLocationSuggestionsAPI(trimmed);
            }, 350); // slightly longer debounce feels smoother
            setDebounceTimer(timer);
        } else {
            setLocationSuggestions([]);
            setShowLocationDropdown(false);
        }
    };

    const handleLocationSelect = async (location: LocationSuggestion) => {
        // const displayString = `${location.name}, ${location.state.name}`;
        const displayString = `${location.name}`;

        const cityData = await getCityBySlug(location.slug);

        const locationData: LocationSuggestion = {
            name: cityData?.name || location.name,
            slug: cityData?.slug || location.slug,
            state: {
                name: location.state.name,
                slug: location.state.slug,
            },
            lat: cityData?.geo?.coordinates?.[1] || 0,
            long: cityData?.geo?.coordinates?.[0] || 0,
            priority: cityData?.priority || 'tier2'
        };

        setLocationCookie(locationData);
        setSelectedLocation(locationData);
        onChange(displayString, locationData);
        setShowLocationDropdown(false);

        window.dispatchEvent(
            new CustomEvent("location-updated", {
                detail: { displayString, location: locationData },
            })
        );
    };

    const showClearIcon = value.trim().length > 0 && !isLoadingLocations;

    return (
        <div ref={wrapperRef} className={`location-search-wrapper relative ${className}`}>
            <CustomInput
                testinputId="location-input"
                value={value}
                placeholder={placeholder}
                leftIcon={
                    <ImageWrapper
                        src="/location.svg"
                        alt="location icon"
                        width={18}
                        height={18}
                        cdnBaseUrl={process.env.NEXT_PUBLIC_CDN_STATIC_URL}
                    />
                }
                rightIcon={
                    <div className="flex items-center gap-2 pr-1">
                        {isLoadingLocations && (
                            <div className="animate-spin h-4 w-4 border-2 border-[#008C8B] border-t-transparent rounded-full" />
                        )}
                        {showClearIcon && (
                            <button
                                type="button"
                                onClick={clearInput}
                                className="p-1 bg-gray-400 hover:bg-gray-500 rounded-full transition-colors cursor-pointer"
                                aria-label="Clear location"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth={2.5}
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                }
                onChange={handleLocationChange}
                error={error}
                aria-expanded={showLocationDropdown}
                aria-autocomplete="list"
                aria-controls="location-suggestions-list"
            />

            {showLocationDropdown && (
                <ul
                    id="location-suggestions-list"
                    className="absolute z-50 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-y-auto mt-1"
                    role="listbox"
                >
                    {locationSuggestions.length > 0 ? (
                        locationSuggestions.map((loc) => (
                            <li key={`${loc?.slug}-${loc?.state?.slug}`}>
                                <button
                                    type="button"
                                    onClick={() => handleLocationSelect(loc)}
                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors text-sm truncate"
                                    role="option"
                                    aria-selected={selectedLocation?.slug === loc?.slug && selectedLocation?.state?.slug === loc?.state?.slug}
                                >
                                    <div className="flex items-center gap-2">
                                        {/* const display = `${loc?.name}, ${loc?.state?.name}`; */}
                                        {/* const display = `${loc?.name}`; */}
                                        <span className="font-medium">{loc?.name}{loc?.state?.name ? `, ${loc?.state?.name}` : ''}</span>
                                    </div>
                                </button>
                            </li>
                        ))
                    ) : (
                        <div className="p-5 text-center text-gray-500">
                            <p className="text-sm font-medium">No cities found</p>
                            <p className="text-xs mt-1 text-gray-400">
                                Try typing your city name (min 3 characters)
                            </p>
                        </div>
                    )}
                </ul>
            )}
        </div>
    );
};

export default LocationSearchInput;